import { z } from "zod";
import type {
  BillingMode,
  BillingOptionStripePrice,
  BillingOptionStripeSync,
  CreatePlanBillingOptionRequest,
  PlanBillingOption,
  UpdatePlanBillingOptionRequest,
} from "@shopexperts/legacy-api";
import { centsToDollars, dollarsToCents, formatPrice } from "@/app/composables/expert-plans/expertPlanForm";

export type BillingIntervalUnit = PlanBillingOption["interval_unit"];

/**
 * View-model for the billing option form. Recurrence follows the open
 * contract (backend D-057): any week/month/year interval with a positive
 * count — quarterly (month × 3) and yearly (year × 1) are just the Phase 0
 * launch cases. Stripe is the final validator at sync time.
 */
// No currency field: the product sells in USD only, so the form omits it and
// the backend applies its configured default. The API contract itself accepts
// any 3-letter code.
export interface BillingOptionFormValues {
  label: string;
  interval_unit: BillingIntervalUnit;
  interval_count: number;
  amount_dollars: number;
}

// Numeric fields coerce because the builder's inputs emit strings.
export const billingOptionFormSchema = z.object({
  label: z
    .string()
    .trim()
    .min(1, "Label is required")
    .max(255, "Keep the label under 255 characters"),
  interval_unit: z.enum(["week", "month", "year"]),
  interval_count: z.coerce
    .number({ message: "Interval is required" })
    .int("Use a whole number of periods")
    .min(1, "Interval must be at least 1")
    .max(65535, "Interval looks too large"),
  amount_dollars: z.coerce
    .number({ message: "Amount is required" })
    .positive("Amount must be greater than zero")
    .max(1_000_000, "Amount looks too large"),
});

export function defaultBillingOptionFormValues(): BillingOptionFormValues {
  return {
    label: "Quarterly",
    interval_unit: "month",
    interval_count: 3,
    amount_dollars: 0,
  };
}

export function billingOptionToFormValues(option: PlanBillingOption): BillingOptionFormValues {
  return {
    label: option.label,
    interval_unit: option.interval_unit,
    interval_count: option.interval_count,
    amount_dollars: centsToDollars(option.amount_cents),
  };
}

export function formValuesToCreateBillingOptionPayload(
  values: BillingOptionFormValues,
): CreatePlanBillingOptionRequest {
  return {
    label: values.label.trim(),
    interval_unit: values.interval_unit,
    interval_count: values.interval_count,
    amount_cents: dollarsToCents(values.amount_dollars),
  };
}

export function formValuesToUpdateBillingOptionPayload(
  values: BillingOptionFormValues,
): UpdatePlanBillingOptionRequest {
  return {
    label: values.label.trim(),
    interval_unit: values.interval_unit,
    interval_count: values.interval_count,
    amount_cents: dollarsToCents(values.amount_dollars),
  };
}

export function currentStripePrice(
  option: PlanBillingOption,
  mode: BillingMode,
): BillingOptionStripePrice | null {
  return option.stripe_prices?.find((price) => price.mode === mode && price.is_current) ?? null;
}

const NAMED_RECURRENCES: Record<string, string> = {
  "week×1": "Weekly",
  "month×1": "Monthly",
  "month×3": "Quarterly",
  "year×1": "Yearly",
};

type BillingRecurrenceLike = Pick<PlanBillingOption, "interval_unit" | "interval_count">;

export function formatRecurrence(option: BillingRecurrenceLike): string {
  const named = NAMED_RECURRENCES[`${option.interval_unit}×${option.interval_count}`];
  if (named) return named;
  return `Every ${option.interval_count} ${option.interval_unit}s`;
}

/** "every 3 months", "every year" — the billing definition as detail copy. */
export function formatBillingInterval(option: BillingRecurrenceLike): string {
  return option.interval_count === 1
    ? `every ${option.interval_unit}`
    : `every ${option.interval_count} ${option.interval_unit}s`;
}

const CADENCE_UNIT_SHORT: Record<BillingRecurrenceLike["interval_unit"], string> = {
  week: "wk",
  month: "mo",
  year: "yr",
};

/** "/mo", "/3 mo" — compact cadence suffix for price displays. */
export function formatCadenceShort(option: BillingRecurrenceLike): string {
  const unit = CADENCE_UNIT_SHORT[option.interval_unit];
  return option.interval_count === 1 ? `/${unit}` : `/${option.interval_count} ${unit}`;
}

export function formatOptionAmount(option: PlanBillingOption): string {
  return formatPrice(option.amount_cents, option.currency);
}

export interface SyncDisplayState {
  label: string;
  badgeClass: string;
}

const SYNC_DISPLAY: Record<BillingOptionStripeSync["status"], SyncDisplayState> = {
  pending: { label: "Sync pending", badgeClass: "bg-amber-100 text-amber-800" },
  synced: { label: "Synced", badgeClass: "bg-green-100 text-green-800" },
  failed: { label: "Sync failed", badgeClass: "bg-red-100 text-red-800" },
};

export function getSyncDisplayState(sync: BillingOptionStripeSync): SyncDisplayState & { mode: BillingMode } {
  return { ...SYNC_DISPLAY[sync.status], mode: sync.mode };
}

/**
 * Pulls the backend's domain or validation message out of a thrown request
 * error. Axios rejects non-2xx before the api client unwraps the envelope,
 * so the human-readable message lives at error.response.data.message. Parsed
 * structurally here because dashboard imports from @shopexperts/legacy-api
 * must stay type-only (runtime exports resolve to the package dist, which
 * lags source until a rebuild).
 */
export function extractBillingApiMessage(error: unknown): string | null {
  if (!error || typeof error !== "object") return null;
  const data = (error as { response?: { data?: unknown } }).response?.data;
  if (data && typeof data === "object") {
    const body = data as { message?: unknown };
    if (typeof body.message === "string" && body.message.trim()) {
      return body.message.trim();
    }
  }
  if (error instanceof Error && error.message) return error.message;
  return null;
}

export function extractBillingApiStatus(error: unknown): number | null {
  if (!error || typeof error !== "object") return null;
  const status = (error as { response?: { status?: unknown } }).response?.status;
  return typeof status === "number" ? status : null;
}

export function extractBillingValidationErrors(error: unknown): Record<string, string> {
  if (!error || typeof error !== "object") return {};
  const data = (error as { response?: { data?: unknown } }).response?.data;
  if (!data || typeof data !== "object") return {};

  const errors = (data as { errors?: unknown }).errors;
  if (!errors || typeof errors !== "object" || Array.isArray(errors)) return {};

  return Object.entries(errors).reduce<Record<string, string>>((mapped, [field, messages]) => {
    const message = Array.isArray(messages)
      ? messages.find((item): item is string => typeof item === "string" && item.trim().length > 0)
      : typeof messages === "string" && messages.trim()
        ? messages
        : null;

    if (message) mapped[field] = message.trim();
    return mapped;
  }, {});
}

export function describeBillingApiError(error: unknown): string {
  const status = extractBillingApiStatus(error);
  if (status !== null && status >= 500) {
    return "The server could not save this plan. Please try again.";
  }
  return extractBillingApiMessage(error) || "An unexpected error occurred";
}
